import type { LinkedDocumentationItemInDto } from 'shared/dtos/in/issue';
import type { DocumentationSignedUrls } from 'shared/domain/documentation/documentationContract';
import { pickFirstNonEmptyString } from 'shared/utils/array';
import { normalizeEntityIdentifier } from 'shared/utils/id/normalizeEntityIdentifier';
import type {
  CreateLinkedDocumentationItemParams,
  LinkedDescriptionMap,
  LinkedDocumentationItem,
  LinkedDocumentationReference,
  LinkedDocumentationResourceMeta,
  LinkedDocPreview,
  MaybeNumber,
  RequestableLinkedDocumentationItem,
} from './types';
import { ResourceLoadStatus } from './types';

export const EMPTY_PREVIEW: LinkedDocPreview = { url: '' };
export const ERROR_PREVIEW: LinkedDocPreview = { url: '#' };

export const LINKED_DOCUMENTATION_CACHE_POLICY = {
  previewTtlMs: 10 * 60 * 1000,
  previewMaxEntries: 200,
  detailsTtlMs: 15 * 60 * 1000,
  detailsMaxEntries: 200,
  retryDelaysMs: [1_000, 3_000, 7_000, 15_000],
} as const;

export function getLinkedDocumentationRetryDelayMs(retryCount: number): number {
  const delays = LINKED_DOCUMENTATION_CACHE_POLICY.retryDelaysMs;
  const lastIndex = delays.length - 1;
  return delays[Math.min(Math.max(retryCount, 0), lastIndex)];
}

export function isUsableLinkedDocumentationResource(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (meta?.status !== ResourceLoadStatus.Ready) {
    return false;
  }

  return meta.expiresAt == null || meta.expiresAt > now;
}

export function canRetryLinkedDocumentationResource(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (meta?.status !== ResourceLoadStatus.Error) {
    return false;
  }

  return meta.nextRetryAt == null || meta.nextRetryAt <= now;
}

export function isExpiredLinkedDocumentationResource(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (meta?.status !== ResourceLoadStatus.Ready) {
    return false;
  }

  return meta.expiresAt != null && meta.expiresAt <= now;
}

export function shouldStartLinkedDocumentationResourceRequest(
  meta: LinkedDocumentationResourceMeta | undefined,
  now: number
): boolean {
  if (!meta || meta.status === ResourceLoadStatus.Idle) {
    return true;
  }

  if (canRetryLinkedDocumentationResource(meta, now)) {
    return true;
  }

  return isExpiredLinkedDocumentationResource(meta, now);
}

function parseAwsDateToMs(value: string): number | undefined {
  if (!/^\d{8}T\d{6}Z$/.test(value)) {
    return undefined;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(9, 11));
  const minute = Number(value.slice(11, 13));
  const second = Number(value.slice(13, 15));

  const timestamp = Date.UTC(year, month, day, hour, minute, second);
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

export function resolveLinkedDocumentationPreviewExpiryMs(
  preview: LinkedDocPreview,
  now = Date.now()
): number {
  const fallbackExpiry = now + LINKED_DOCUMENTATION_CACHE_POLICY.previewTtlMs;
  const urlCandidate =
    preview.url || preview.imageSignedRequest || preview.thumbnailSignedRequest;

  if (!urlCandidate) {
    return fallbackExpiry;
  }

  try {
    const parsedUrl = new URL(urlCandidate);
    const expires = Number(
      parsedUrl.searchParams.get('X-Amz-Expires') ||
        parsedUrl.searchParams.get('x-amz-expires')
    );
    const dateRaw =
      parsedUrl.searchParams.get('X-Amz-Date') ||
      parsedUrl.searchParams.get('x-amz-date');
    const startAt = dateRaw ? parseAwsDateToMs(dateRaw) : undefined;

    if (Number.isFinite(expires) && startAt) {
      return startAt + expires * 1000;
    }
  } catch {
    return fallbackExpiry;
  }

  return fallbackExpiry;
}

/** The preview of a linked documentation version from its signed URLs. */
export function documentationSignedUrlsToPreview(urls: DocumentationSignedUrls): LinkedDocPreview {
  const { signedRequest, imageRotation, ...optionalUrls } = urls;
  return {
    url: signedRequest,
    ...optionalUrls,
    ...(imageRotation !== undefined ? { imageRotation } : {}),
    viaServiceWorker: true,
  };
}

export function getRequestableLinkedDocumentationItems(
  items: LinkedDocumentationItem[]
): RequestableLinkedDocumentationItem[] {
  return items.filter(
    (item): item is RequestableLinkedDocumentationItem =>
      Boolean(item.documentationId && item.versionId != null)
  );
}

function normalizeFiniteNumber(value: MaybeNumber): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

export function resolveLinkedDocumentationImageRotation(
  candidates: MaybeNumber[]
): number | undefined {
  for (const candidate of candidates) {
    const normalized = normalizeFiniteNumber(candidate);
    if (normalized != null) return normalized;
  }

  return undefined;
}

export function createLinkedDocumentationItem({
  documentationId,
  versionId,
  sourceDocumentId,
  nameCandidates,
  descriptionCandidates,
  imageRotationCandidates,
}: CreateLinkedDocumentationItemParams): LinkedDocumentationItem {
  const name = pickFirstNonEmptyString(nameCandidates, documentationId);
  const description = pickFirstNonEmptyString(descriptionCandidates ?? []);

  return {
    name,
    ...(description ? { description } : {}),
    documentationId,
    versionId,
    ...(sourceDocumentId ? { sourceDocumentId } : {}),
    imageRotation: resolveLinkedDocumentationImageRotation(
      imageRotationCandidates ?? []
    ),
    deleted: false,
  };
}

export function hasLinkedDocumentationReference(
  linkedDocumentation: LinkedDocumentationItem[],
  documentationId: string,
  versionId: string
): boolean {
  return linkedDocumentation.some(
    (item) =>
      item.documentationId === documentationId && item.versionId === versionId
  );
}

export function getLinkedDocumentationItemKey(
  item: LinkedDocumentationItem
): string | undefined {
  const documentationId =
    item.documentationId != null ? String(item.documentationId) : '';
  const versionId = item.versionId != null ? String(item.versionId) : '';

  if (!documentationId || !versionId) {
    return undefined;
  }

  return `${documentationId}:${versionId}`;
}

/**
 * Keeps server-linked items and preserves local-only links until the next sync
 * includes them (e.g. right after export-to-documentation).
 */
export function mergeLinkedDocumentationItems(
  localItems: LinkedDocumentationItem[] | undefined,
  remoteItems: LinkedDocumentationItem[] | undefined
): LinkedDocumentationItem[] {
  const mergedByKey = new Map<string, LinkedDocumentationItem>();

  (remoteItems ?? [])
    .filter((item) => !item.deleted)
    .forEach((item) => {
      const key = getLinkedDocumentationItemKey(item);
      if (key) {
        mergedByKey.set(key, item);
      }
    });

  (localItems ?? [])
    .filter((item) => !item.deleted)
    .forEach((item) => {
      const key = getLinkedDocumentationItemKey(item);
      if (key && !mergedByKey.has(key)) {
        mergedByKey.set(key, item);
      }
    });

  return Array.from(mergedByKey.values());
}

export function doesLinkedDocumentationMatchReference(
  item: LinkedDocumentationItem,
  reference: LinkedDocumentationReference
): boolean {
  if (
    reference.linkedDocumentationId &&
    item._id === reference.linkedDocumentationId
  ) {
    return true;
  }

  if (!reference.documentationId || !reference.versionId) {
    return false;
  }

  return (
    item.documentationId === reference.documentationId &&
    item.versionId === reference.versionId
  );
}

export function findLinkedDocumentationByReference(
  linkedDocumentation: LinkedDocumentationItem[],
  reference: LinkedDocumentationReference
): LinkedDocumentationItem | undefined {
  return linkedDocumentation.find((item) =>
    doesLinkedDocumentationMatchReference(item, reference)
  );
}

export function resolveLinkedDocumentationIdentifier(
  linkedDocumentation: LinkedDocumentationItem[],
  reference: LinkedDocumentationReference
): string | undefined {
  const matchedItem = findLinkedDocumentationByReference(
    linkedDocumentation,
    reference
  );

  return matchedItem?._id ?? reference.linkedDocumentationId;
}

export function linkedDocumentationItemInDtoToItem(
  dto: LinkedDocumentationItemInDto,
  descriptionBySourceDocumentId?: LinkedDescriptionMap
): LinkedDocumentationItem {
  const normalizedId =
    normalizeEntityIdentifier(dto._id) ?? normalizeEntityIdentifier(dto.id);
  const normalizedDocumentationId = normalizeEntityIdentifier(
    dto.documentationId
  );
  const normalizedVersionId = normalizeEntityIdentifier(dto.versionId);
  const normalizedSourceDocumentId = normalizeEntityIdentifier(
    dto.sourceDocumentId
  );

  const name = pickFirstNonEmptyString([
    dto.name,
    dto.documentationName,
    dto.label,
  ]);

  const descriptionFromSourceDocument =
    dto.sourceDocumentId != null
      ? descriptionBySourceDocumentId?.[String(dto.sourceDocumentId)]
      : undefined;

  const resolvedDescription = String(
    dto.description ?? descriptionFromSourceDocument ?? ''
  ).trim();

  return {
    name,
    ...(resolvedDescription ? { description: resolvedDescription } : {}),
    _id: normalizedId,
    documentationId: normalizedDocumentationId,
    versionId: normalizedVersionId,
    imageRotation: dto.imageRotation,
    sourceDocumentId: normalizedSourceDocumentId,
    createdAt: dto.createdAt,
    createdBy: normalizeEntityIdentifier(dto.createdBy),
    documentationName: dto.documentationName,
    label: dto.label,
    deleted: dto.deleted ?? false,
  };
}

export function linkedDocumentationInDtoToItems(
  dtos: LinkedDocumentationItemInDto[] | undefined,
  descriptionBySourceDocumentId?: LinkedDescriptionMap
): LinkedDocumentationItem[] {
  return (dtos ?? [])
    .filter((dto) => !dto.deleted)
    .map((dto) =>
      linkedDocumentationItemInDtoToItem(dto, descriptionBySourceDocumentId)
    );
}
